import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import useSelectedTenant from '../../../../../components/hooks/useSelectedTenant';
import useCurrencyService from '../../../../../components/hooks/useCurrencyService';
import SummaryTiles, { TILE_ACCENT } from '../../../../../components/utils/SummaryTiles';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import { GetServiceFeesBIZcore } from '../../../../../redux/actions/business/core/serviceFeesActions';
import { GetInternationalServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalServiceFeesActions';
import { GetInternationalUSDtoUSDServiceFeesBIZcore } from '../../../../../redux/actions/business/core/internationalUSDtoUSDServiceFeesActions';
import { GetServicesBIZsubs } from '../../../../../redux/actions/business/subscriptions/servicesActions';
import { GetSubscriptionPlansBIZsubs } from '../../../../../redux/actions/business/subscriptions/subscriptionPlansActions';
import { GetCompaniesBIZidentity } from '../../../../../redux/actions/business/identity/companiesActions';

const LABEL_COLOR = 'rgba(226, 242, 249, 0.6)';

// The two sides of this screen live in different microservices and therefore
// different databases, so there is no FK to lean on: identity owns the service
// DEFINITION (services.id, its plan/product/delivery method) and core owns the
// PRICING, keyed by that same id stored as a string in ref_service_id.
// Everything below joins the two in the browser — nothing is written, and no
// cross-service coupling is introduced on either backend.
const FEE_TABLES = [
  { key: 'general', label: 'General', route: '/business/core/tenants/service-fees' },
  { key: 'international', label: 'International', route: '/business/core/tenants/international-service-fees' },
  { key: 'usd', label: 'USD → USD', route: '/business/core/tenants/international-usd-to-usd-service-fees' },
];

const BillingExplorerPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tenantId = useSelectedTenant();
  const { handleShowAmount } = useCurrencyService();

  const coreState = useSelector(state => state["business.core"]);
  const subsState = useSelector(state => state["business.subscriptions"]);
  const identityState = useSelector(state => state["business.identity"]);

  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    handleGetAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function handleGetAll() {
    setLoading(true);
    // Pulled unpaginated (large page size) because the join has to happen over
    // the whole set — a page at a time would produce false "no fee" results.
    await Promise.all([
      dispatch(GetServicesBIZsubs(1, 1000, "", () => {})),
      dispatch(GetSubscriptionPlansBIZsubs(1, 1000, "", () => {})),
      dispatch(GetCompaniesBIZidentity(1, 1000, "", () => {})),
      dispatch(GetServiceFeesBIZcore(tenantId, 1, 1000, "", () => {})),
      dispatch(GetInternationalServiceFeesBIZcore(tenantId, 1, 1000, "", () => {})),
      dispatch(GetInternationalUSDtoUSDServiceFeesBIZcore(tenantId, 1, 1000, "", () => {})),
    ]);
    setLoading(false);
  }

  const services = useMemo(() => subsState?.servicesList?.records || [], [subsState]);
  const plans = useMemo(() => subsState?.subscriptionPlansList?.records || [], [subsState]);
  const companies = useMemo(() => identityState?.companiesList?.records || [], [identityState]);

  const feesByTable = useMemo(() => ({
    general: coreState?.serviceFeesList?.records || [],
    international: coreState?.internationalServiceFeesList?.records || [],
    usd: coreState?.internationalUSDtoUSDServiceFeesList?.records || [],
  }), [coreState]);

  // service id (string, as core stores it) -> matching fee rows per table
  const feeIndex = useMemo(() => {
    const index = {};
    FEE_TABLES.forEach(({ key }) => {
      feesByTable[key].forEach((row) => {
        const id = String(row.ref_service_id ?? "").trim();
        if (!index[id]) index[id] = { general: [], international: [], usd: [] };
        index[id][key].push(row);
      });
    });
    return index;
  }, [feesByTable]);

  // company -> plan comes from identity; a company with no plan cannot be billed
  const selectedCompany = companies.find((c) => c.id === companyFilter);
  const effectivePlanId = companyFilter
    ? (selectedCompany?.ref_plan_id ?? null)
    : (planFilter === "" ? null : Number(planFilter));

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return services
      .filter((s) => effectivePlanId === null || s.ref_plan_id === effectivePlanId)
      .filter((s) => !term
        || String(s.id) === term
        || (s.service_code || "").toLowerCase().includes(term))
      .map((s) => {
        const fees = feeIndex[String(s.id)] || { general: [], international: [], usd: [] };
        const total = fees.general.length + fees.international.length + fees.usd.length;
        return { service: s, fees, total };
      })
      .sort((a, b) => a.service.id - b.service.id);
  }, [services, feeIndex, effectivePlanId, search]);

  // Fee rows pointing at a service id identity does not know about. Because the
  // link is a plain string across two databases, nothing stops this drifting.
  const knownIds = useMemo(() => new Set(services.map((s) => String(s.id))), [services]);
  const orphans = useMemo(() => {
    const out = [];
    FEE_TABLES.forEach(({ key, label }) => {
      feesByTable[key].forEach((row) => {
        const id = String(row.ref_service_id ?? "").trim();
        if (!knownIds.has(id)) out.push({ table: label, id: id || "(blank)", name: row.fee_name });
      });
    });
    return out;
  }, [feesByTable, knownIds]);

  const unpriced = rows.filter((r) => r.total === 0);

  const planName = (id) => plans.find((p) => p.id === id)?.plan_name || null;

  const summaryTiles = [
    { label: "Services in view", value: rows.length, accent: TILE_ACCENT.neutral },
    { label: "Priced", value: rows.length - unpriced.length, accent: TILE_ACCENT.good },
    { label: "No fee defined", value: unpriced.length, accent: unpriced.length ? TILE_ACCENT.bad : TILE_ACCENT.good },
    { label: "Orphan fee rows", value: orphans.length, accent: orphans.length ? TILE_ACCENT.bad : TILE_ACCENT.good },
  ];

  // One-line pricing summary per table, so the whole picture fits on a row.
  const describeFees = (key, list) => {
    if (list.length === 0) return null;
    if (key === 'general') {
      return list.map((f, i) => (
        <Typography key={i} variant='body2'>
          {Number(f.fee_percent) > 0 ? `${f.fee_percent}%` : handleShowAmount(f.fee_currency, f.fee_amount || 0)}
          {!f.is_active && <Chip size='small' label="inactive" color='warning' variant='outlined' sx={{ ml: "6px" }} />}
        </Typography>
      ));
    }
    // international / usd are laddered — summarise rather than list every band
    const lanes = [...new Set(list.map((f) => f.fee_name || "(unnamed)"))];
    return (
      <Box>
        <Typography variant='body2'>
          {list.length} band{list.length === 1 ? "" : "s"} across {lanes.length} lane{lanes.length === 1 ? "" : "s"}
        </Typography>
        <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
          {lanes.join(", ")}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <TopBar
        title={"Billing Explorer"}
        search={search}
        searchPlaceholder={"Service id or service code"}
        onSearchChange={(e) => setSearch(e.target.value)}
        button1Text={"Refresh"}
        button1OnClick={() => handleGetAll()}
        />

      <Typography
        variant='caption'
        sx={{ color: LABEL_COLOR, display: "block", mb: "16px" }}>
        Joins the identity service catalogue to this tenant&apos;s core pricing on
        the service id. Pick a company to resolve its plan automatically, or filter
        by plan, or search a service id directly. Read-only.
      </Typography>

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
        <Box>
          {/* ---------- selectors ---------- */}
          <Box className='flexCenter_Row' flexWrap={"wrap"} gap={"12px"} mb={"16px"}>
            <TextField
              select
              size='small'
              label='Company'
              sx={{ minWidth: "240px" }}
              value={companyFilter}
              onChange={(e) => {
                setCompanyFilter(e.target.value);
                setPlanFilter("");
              }}>
              <MenuItem value=''>All companies</MenuItem>
              {
                [...companies]
                  .sort((a, b) => (a.legal_name || "").localeCompare(b.legal_name || ""))
                  .map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.legal_name || c.id}</MenuItem>
                  ))
              }
            </TextField>

            <TextField
              select
              size='small'
              label='Plan'
              sx={{ minWidth: "200px" }}
              value={companyFilter ? String(selectedCompany?.ref_plan_id ?? "") : planFilter}
              disabled={!!companyFilter}
              onChange={(e) => setPlanFilter(e.target.value)}>
              <MenuItem value=''>All plans</MenuItem>
              {
                [...plans]
                  .sort((a, b) => (a.plan_name || "").localeCompare(b.plan_name || ""))
                  .map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>{p.plan_name}</MenuItem>
                  ))
              }
            </TextField>

            {
              companyFilter
              &&
              <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                {
                  selectedCompany?.ref_plan_id
                    ? `Plan resolved from company: ${planName(selectedCompany.ref_plan_id) || selectedCompany.ref_plan_id}`
                    : "This company has no plan assigned — it cannot be billed."
                }
              </Typography>
            }
          </Box>

          <SummaryTiles tiles={summaryTiles} />

          {/* ---------- orphan fee rows ---------- */}
          {
            orphans.length > 0
            &&
            <Box
              sx={{
                border: `1px solid ${TILE_ACCENT.bad}40`,
                background: `${TILE_ACCENT.bad}14`,
                borderRadius: "12px",
                padding: "12px 16px",
                mb: "16px",
              }}>
              <Box className='flexCenter_Row' gap={"10px"} mb={"6px"}>
                <ReportProblemOutlinedIcon sx={{ color: TILE_ACCENT.bad }} />
                <Typography variant='body2'>
                  <b>{orphans.length}</b> fee row{orphans.length === 1 ? "" : "s"} point at a
                  service id that does not exist in identity
                </Typography>
              </Box>
              {
                orphans.slice(0, 8).map((o, i) => (
                  <Typography key={i} variant='caption' sx={{ color: LABEL_COLOR, display: "block" }}>
                    {o.table} · service id {o.id} · {o.name || "(unnamed)"}
                  </Typography>
                ))
              }
              {
                orphans.length > 8
                &&
                <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                  …and {orphans.length - 8} more
                </Typography>
              }
            </Box>
          }

          {/* ---------- the join ---------- */}
          {
            rows.length === 0
            ?
            <Box height={"200px"} className="flexCenterCenterRow">
              <Typography>No service matches this filter</Typography>
            </Box>
            :
            <Table className="block">
              <TableHead>
                <TableRow>
                  <TableCell>Service</TableCell>
                  <TableCell>Plan / Product</TableCell>
                  <TableCell>Delivery</TableCell>
                  {FEE_TABLES.map((t) => <TableCell key={t.key}>{t.label} fee</TableCell>)}
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  rows.map(({ service, fees, total }) => (
                    <TableRow
                      key={service.id}
                      sx={{
                        borderLeft: `3px solid ${total === 0 ? TILE_ACCENT.bad : "transparent"}`,
                      }}>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {service.service_code || <NA />}
                        </Typography>
                        <Typography variant='caption' color={"grey"}>
                          id&nbsp;<Copyable>{String(service.id)}</Copyable>
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='body2'>{planName(service.ref_plan_id) || <NA />}</Typography>
                        <Typography variant='caption' sx={{ color: LABEL_COLOR }}>
                          {service.ref_product_id?.product_name
                            || service.product?.product_name
                            || service.ref_product_id
                            || <NA />}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant='caption' sx={{ fontFamily: "monospace" }}>
                          {service.ref_delivery_method_id?.delivery_method
                            || service.delivery_method?.delivery_method
                            || service.ref_delivery_method_id
                            || <NA />}
                        </Typography>
                      </TableCell>
                      {
                        FEE_TABLES.map((t) => (
                          <TableCell key={t.key}>
                            {
                              fees[t.key].length === 0
                              ? <Typography variant='caption' sx={{ color: LABEL_COLOR }}>—</Typography>
                              :
                              <Tooltip title={`Open the ${t.label} fees table`}>
                                <Box
                                  onClick={() => navigate(t.route)}
                                  sx={{ cursor: "pointer" }}>
                                  {describeFees(t.key, fees[t.key])}
                                </Box>
                              </Tooltip>
                            }
                          </TableCell>
                        ))
                      }
                      <TableCell>
                        {
                          total === 0
                          ? <Chip size='small' label="No fee" color='error' variant='filled' />
                          : <Chip size='small' label={`${total} row${total === 1 ? "" : "s"}`} color='success' variant='outlined' />
                        }
                      </TableCell>
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          }

          {
            rows.length > 0 && unpriced.length === 0 && orphans.length === 0
            &&
            <Box className='flexCenter_Row' gap={"10px"} mt={"14px"}>
              <CheckCircleOutlineOutlinedIcon sx={{ color: TILE_ACCENT.good }} />
              <Typography variant='body2'>
                Every service in view has pricing, and every fee row resolves to a real service.
              </Typography>
              <ChevronRightIcon sx={{ color: LABEL_COLOR }} />
            </Box>
          }
        </Box>
      }
    </Box>
  )
}

export default BillingExplorerPage;
